/**
 * Node definition registry.
 * Maps node types to their definitions for scene JSON conversion.
 */

import type { NodeDefinition, SceneJsonContext } from './types';
import { conditionDefinitions } from './conditions';
import { actionDefinitions } from './actions';
import { FLOW_CARD_DEFINITIONS } from '../../core/card-engine/registry';
import { getFlowCardDefinition } from '../../core/card-engine/registry';

const templateNodeDefinitions = Array.from(new Set(FLOW_CARD_DEFINITIONS.map((card) => card.type))).map(
	(type) => {
		const fallback = getFlowCardDefinition(type)!;
		return [
			type,
			{
				type,
				category: fallback.role,
				defaultData: fallback.defaults,
				sourceHandles: fallback.ports.sources,
				toSceneJson: (node, context) =>
					getFlowCardDefinition(type, String(node.data.paletteKey || ''))?.compile(
						node,
						context,
					) ?? null,
			},
		] as [string, NodeDefinition];
	},
);

const nodeDefinitions = new Map<string, NodeDefinition>([
	...conditionDefinitions,
	...actionDefinitions,
	...templateNodeDefinitions,
]);

export function getNodeDefinition(type: string): NodeDefinition | undefined {
	return nodeDefinitions.get(type);
}

/**
 * Convert a node to its scene JSON representation.
 * For conditions: returns if-block condition JSON.
 * For actions: returns do-block action JSON (without delay wrapper).
 */
export function nodeToSceneJson(
	node: { type: string; data: any; id: string },
	context?: SceneJsonContext,
): any {
	const def = getNodeDefinition(node.type);
	if (!def) return null;
	return def.toSceneJson(node as any, context);
}

export { nodeDefinitions };
